import React, { useState, useEffect, useContext, useRef, useCallback } from "react"
import { AuthContext } from "../../utils/providers/auth"
import { CALENDAR_MONTHS } from "../../utils/exports/months"
import { auth, db } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import "./itemised-stats.module.scss"

// UI imports
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Select from "../../components/ui/select/select"
import Button from "../../components/ui/button/button"
import Table from "../../components/structure/table/table"
import Booking from "../../components/itemised-stats/booking"
import Checkbox from "../../components/ui/checkbox/checkbox"

// Returns the itemised stats page
export default function ItemisedStats() {
	const [loading, setLoading] = useState(false)
	const [bookings, setBookings] = useState([])
	const [clientsMap, setClientsMap] = useState({})
	const [agentsMapPrefetch, setAgentsMapPrefetch] = useState({})
	const [documentsMap, setDocumentsMap] = useState({})
	const [supplierInvoicesMap, setSupplierInvoicesMap] = useState({})
	const [hotelsMap, setHotelsMap] = useState({})
	const [coursesMap, setCoursesMap] = useState({})
	const [transfersMap, setTransfersMap] = useState({})
	const [bookingHotelsMap, setBookingHotelsMap] = useState({})
	const [bookingRoundsMap, setBookingRoundsMap] = useState({})

	const [exportArr, setExportArr] = useState([])
	const [exportTitle, setExportTitle] = useState("")

	const [agents, setAgents] = useState({})
	const [years, setYears] = useState({})

	// Filtering options
	const [agent, setAgent] = useState("")
	const [month, setMonth] = useState("")
	const [year, setYear] = useState("")
	const [type, setType] = useState("")

	const [flightsOnly, setFlightsOnly] = useState(false)
	const [hideWithDocuments, setHideWithDocuments] = useState(false)

	// Column visibility toggles (default: off for specified columns)
	const [showSite, setShowSite] = useState(false)
	const [showAgent, setShowAgent] = useState(false)
	const [showLeadPax, setShowLeadPax] = useState(false)
	const [showEmail, setShowEmail] = useState(false)
	const [showReturning, setShowReturning] = useState(false)
	const [showNoPax, setShowNoPax] = useState(false)
	const [showLocation, setShowLocation] = useState(false)
	const [showItemisedInvoices, setShowItemisedInvoices] = useState(false)

	const [flightsCost, setFlightsCost] = useState(0)
	const [customerPrice, setCustomerPrice] = useState(0)
	const [paidByClient, setPaidByClient] = useState(0)
	const [supplierInvoices, setSupplierInvoices] = useState(0)
	const [margins, setMargins] = useState(0)
	const [commission, setCommission] = useState(0)

	const [newMargins, setNewMargins] = useState(0)
	const [newCommission, setNewCommission] = useState(0)

	const [paxFlying, setPaxFlying] = useState(0)
	const [paxNotFlying, setPaxNotFlying] = useState(0)

	const totalMarginStatsFigure = useRef({})
	const totalCommisionStatsFigure = useRef({})

	const paxWithFlights = useRef({})
	const paxWithoutFlights = useRef({})

	// Request management refs
	const currentRequestRef = useRef(null)
	const debounceTimerRef = useRef(null)
	const isRequestInProgressRef = useRef(false)

	// Get the current user auth from the context
	const { user } = useContext(AuthContext)

	// Cancel any ongoing requests
	const cancelCurrentRequest = useCallback(() => {
		if (currentRequestRef.current) {
			currentRequestRef.current.cancel()
			currentRequestRef.current = null
		}
	}, [])

	// Clear debounce timer
	const clearDebounceTimer = useCallback(() => {
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current)
			debounceTimerRef.current = null
		}
	}, [])

	const clearLocalFigures = () => {
		totalMarginStatsFigure.current = {}
		totalCommisionStatsFigure.current = {}
		paxWithFlights.current = {}
		paxWithoutFlights.current = {}

		setNewMargins(0)
		setNewCommission(0)
		setPaxFlying(0)
		setPaxNotFlying(0)
	}

	// When the bookings are updated
	useEffect(() => {
		// Return early if there are no bookings
		if (bookings.length === 0 || !user?.can_export) return

		// Build a file name from the parameters
		setExportTitle(`${agent ? agents[agent.option] : "AllAgents"}_${month ? month : "All"}${year}_${type === "booked_date" ? "ByBookedDate" : "ByTravelDate"}${flightsOnly ? "_FlightsOnly" : ""}`)

		// Otherwise, loop through the bookings
		const exportData = bookings.map((booking) => {
			// Deconstruct the booking details
			const { booked, reference, check_in, golfers, non_golfers, booked_location, margins, confirmed, resorts_paid, paid_by_client, flights_booked, enquiry } = booking

			// Deconstruct the margins
			const { customer_price, flights_cost, supplier_invoices, agents_margin } = margins || {}

			// Return the booking details
			return {
				booked: moment(booked?.seconds, "X").format("MMM D[,] YYYY"),
				reference,
				site: enquiry?.site,
				check_in: moment(check_in?.seconds, "X").format("MMM D[,] YYYY"),
				non_flying_pax: flights_cost === 0 ? golfers + non_golfers : 0,
				flying_pax: flights_cost > 0 ? golfers + non_golfers : 0,
				location: booked_location,
				flights_cost: flights_cost || 0,
				flights_booked: flights_booked ? "TRUE" : "FALSE",
				customer_price: customer_price || 0,
				paid_by_client: paid_by_client || 0,
				supplier_invoices: supplier_invoices || 0,
				sales_margin: customer_price - supplier_invoices,
				commission: agents_margin || 0,
				confirmed: confirmed ? "TRUE" : "FALSE",
				resorts_paid: resorts_paid ? "TRUE" : "FALSE",
			}
		})

		// Compute the totals for numeric fields
		const totals = {
			booked: "Totals",
			reference: "",
			site: "",
			check_in: "",
			non_flying_pax: exportData.reduce((sum, item) => sum + (item.non_flying_pax || 0), 0),
			flying_pax: exportData.reduce((sum, item) => sum + (item.flying_pax || 0), 0),
			location: "",
			flights_cost: `£${exportData.reduce((sum, item) => sum + (item.flights_cost || 0), 0).toLocaleString()}`,
			flights_booked: "",
			customer_price: `£${exportData.reduce((sum, item) => sum + (item.customer_price || 0), 0).toLocaleString()}`,
			paid_by_client: `£${exportData.reduce((sum, item) => sum + (item.paid_by_client || 0), 0).toLocaleString()}`,
			supplier_invoices: `£${exportData.reduce((sum, item) => sum + (item.supplier_invoices || 0), 0).toLocaleString()}`,
			sales_margin: `£${exportData.reduce((sum, item) => sum + (item.sales_margin || 0), 0).toLocaleString()}`,
			commission: `£${exportData.reduce((sum, item) => sum + (item.commission || 0), 0).toLocaleString()}`,
			confirmed: "",
			resorts_paid: "",
		}

		// Add an empty row to the end of the export data
		exportData.push({})

		// Add the totals row
		exportData.push(totals)

		// Set the export data into the state
		setExportArr(exportData)
	}, [bookings])

	// Helper function to batch process promises with concurrency control
	const batchProcessPromises = async (items, processFunc, batchSize = 10) => {
		const results = []
		for (let i = 0; i < items.length; i += batchSize) {
			const batch = items.slice(i, i + batchSize)
			const batchResults = await Promise.all(
				batch.map((item) =>
					processFunc(item).catch((error) => {
						console.error(`Error processing item:`, error)
						return null
					})
				)
			)
			results.push(...batchResults)
		}
		return results
	}

	// Batch fetch hotels for all bookings
	const fetchHotelsForBookings = async (bookingIds) => {
		const BATCH_SIZE = 10
		const hotelsMap = {}
		for (let i = 0; i < bookingIds.length; i += BATCH_SIZE) {
			const batch = bookingIds.slice(i, i + BATCH_SIZE)
			const batchResults = await Promise.all(
				batch.map((bookingId) =>
					db
						.collection(`bookings/${bookingId}/hotels`)
						.get()
						.then((snap) => ({
							bookingId,
							hotels: snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
						}))
						.catch(() => ({ bookingId, hotels: [] }))
				)
			)
			batchResults.forEach(({ bookingId, hotels }) => {
				hotelsMap[bookingId] = hotels
			})
		}
		return hotelsMap
	}

	// Batch fetch rounds for all bookings
	const fetchRoundsForBookings = async (bookingIds) => {
		const BATCH_SIZE = 10
		const roundsMap = {}
		for (let i = 0; i < bookingIds.length; i += BATCH_SIZE) {
			const batch = bookingIds.slice(i, i + BATCH_SIZE)
			const batchResults = await Promise.all(
				batch.map((bookingId) =>
					db
						.collection(`bookings/${bookingId}/rounds`)
						.get()
						.then((snap) => ({
							bookingId,
							rounds: snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
						}))
						.catch(() => ({ bookingId, rounds: [] }))
				)
			)
			batchResults.forEach(({ bookingId, rounds }) => {
				roundsMap[bookingId] = rounds
			})
		}
		return roundsMap
	}

	// Prefetch clients, agents, documents, and supplier invoices for current bookings to reduce per-row requests
	useEffect(() => {
		const prefetch = async () => {
			if (!bookings || bookings.length === 0) {
				setClientsMap({})
				setAgentsMapPrefetch({})
				setDocumentsMap({})
				setSupplierInvoicesMap({})
				setBookingHotelsMap({})
				setBookingRoundsMap({})
				return
			}

			try {
				// Collect unique IDs
				const clientIds = showLeadPax || showEmail ? Array.from(new Set(bookings.map((b) => b.client).filter(Boolean))) : []
				const agentIds = showAgent ? Array.from(new Set(bookings.map((b) => b.agent).filter(Boolean))) : []
				const bookingIds = bookings.map((b) => b.id).filter(Boolean)

				// Build promises array conditionally
				const promises = []

				// Clients (only if Lead Pax or Email columns are visible)
				promises.push(showLeadPax || showEmail ? (clientIds.length > 0 ? batchProcessPromises(clientIds, (id) => db.doc(`clients/${id}`).get(), 20) : Promise.resolve([])) : Promise.resolve([]))

				// Agents (only if Agent column is visible)
				promises.push(showAgent ? (agentIds.length > 0 ? batchProcessPromises(agentIds, (id) => db.doc(`users/${id}`).get(), 20) : Promise.resolve([])) : Promise.resolve([]))

				// Documents (always fetch)
				promises.push(fetchDocumentsForReferences(bookings.map((b) => b.reference).filter(Boolean)))

				// Supplier invoices (always fetch)
				promises.push(fetchSupplierInvoicesForBookings(bookingIds))

				// Booking hotels and rounds (always fetch for itemised invoices)
				promises.push(fetchHotelsForBookings(bookingIds))
				promises.push(fetchRoundsForBookings(bookingIds))

				const [clientDocs, agentDocs, newDocumentsMap, newSupplierInvoicesMap, newBookingHotelsMap, newBookingRoundsMap] = await Promise.all(promises)

				// Build clients map
				const newClientsMap = {}
				if (clientDocs && clientDocs.length > 0) {
					clientDocs.forEach((doc) => {
						if (doc && doc.exists) newClientsMap[doc.id] = doc.data()
					})
				}

				// Build agents map
				const newAgentsMap = {}
				if (agentDocs && agentDocs.length > 0) {
					agentDocs.forEach((doc) => {
						if (doc && doc.exists) newAgentsMap[doc.id] = doc.data()
					})
				}

				setClientsMap(newClientsMap)
				setAgentsMapPrefetch(newAgentsMap)
				setDocumentsMap(newDocumentsMap)
				setSupplierInvoicesMap(newSupplierInvoicesMap)
				setBookingHotelsMap(newBookingHotelsMap)
				setBookingRoundsMap(newBookingRoundsMap)
			} catch (e) {
				console.error("Prefetch failed", e)
			}
		}

		prefetch()
	}, [bookings, showLeadPax, showEmail, showAgent, showReturning])

	// Batch fetch documents for multiple booking references
	const fetchDocumentsForReferences = async (references) => {
		if (references.length === 0) return {}

		try {
			// Firestore has a limit of 10 items for 'in' queries, so we need to batch
			const BATCH_SIZE = 10

			// Create both string and integer versions of all references
			const stringReferences = references.map((ref) => String(ref))
			const intReferences = references.map((ref) => parseInt(ref)).filter((ref) => !isNaN(ref))

			// Create batches for string references
			const stringBatches = []
			for (let i = 0; i < stringReferences.length; i += BATCH_SIZE) {
				stringBatches.push(stringReferences.slice(i, i + BATCH_SIZE))
			}

			// Create batches for integer references
			const intBatches = []
			for (let i = 0; i < intReferences.length; i += BATCH_SIZE) {
				intBatches.push(intReferences.slice(i, i + BATCH_SIZE))
			}

			// Execute all batches in parallel (both string and int queries)
			const stringSnapshots = await Promise.all(stringBatches.map((batch) => db.collection("invoice_documents").where("connected_references", "array-contains-any", batch).get()))

			const intSnapshots = await Promise.all(intBatches.map((batch) => db.collection("invoice_documents").where("connected_references", "array-contains-any", batch).get()))

			// Combine all snapshots
			const allDocSnapshots = [...stringSnapshots, ...intSnapshots]

			// Build a map of reference -> documents
			const documentsMap = {}

			allDocSnapshots.forEach((snapshot) => {
				snapshot.docs.forEach((doc) => {
					const docData = { id: doc.id, ...doc.data() }
					const connectedRefs = docData.connected_references || []

					// Add this document to each reference it's connected to
					connectedRefs.forEach((ref) => {
						const refKey = String(ref) // Normalize to string
						if (!documentsMap[refKey]) {
							documentsMap[refKey] = []
						}
						// Avoid duplicates
						if (!documentsMap[refKey].some((d) => d.id === docData.id)) {
							documentsMap[refKey].push(docData)
						}
					})
				})
			})

			return documentsMap
		} catch (e) {
			console.error("Error fetching documents in batch:", e)
			return {}
		}
	}

	// Batch fetch supplier invoices for multiple bookings with concurrency control
	const fetchSupplierInvoicesForBookings = async (bookingIds) => {
		if (bookingIds.length === 0) return {}

		try {
			// Process requests in batches to avoid "too many outstanding requests" error
			const BATCH_SIZE = 10 // Process 10 bookings at a time
			const invoicesMap = {}

			// Split bookingIds into batches
			for (let i = 0; i < bookingIds.length; i += BATCH_SIZE) {
				const batch = bookingIds.slice(i, i + BATCH_SIZE)

				// Fetch invoices for this batch in parallel
				const batchSnapshots = await Promise.all(
					batch.map((bookingId) =>
						db
							.collection(`bookings/${bookingId}/supplier_invoices`)
							.orderBy("created", "desc")
							.get()
							.catch((error) => {
								console.error(`Error fetching invoices for booking ${bookingId}:`, error)
								return { docs: [] } // Return empty result on error
							})
					)
				)

				// Process this batch's results
				batchSnapshots.forEach((snapshot, index) => {
					const bookingId = batch[index]
					invoicesMap[bookingId] = snapshot.docs.map((doc) => ({
						id: doc.id,
						...doc.data(),
					}))
				})
			}

			return invoicesMap
		} catch (e) {
			console.error("Error fetching supplier invoices in batch:", e)
			return {}
		}
	}

	/**
	 * On load for the context
	 */
	useEffect(() => {
		// Cancel any ongoing requests when component unmounts or user changes
		return () => {
			cancelCurrentRequest()
			clearDebounceTimer()
		}
	}, [cancelCurrentRequest, clearDebounceTimer])

	// Fetch agents with proper error handling and request management
	const fetchAgents = useCallback(async () => {
		try {
			let agentsArr = {}

			if (user?.is_admin) {
				const agentDocs = await db.collection("users").orderBy("first_name").get()

				agentDocs.forEach((agentDoc) => {
					const { first_name, last_name } = agentDoc.data()
					agentsArr[agentDoc.id] = `${first_name} ${last_name}`
				})
			} else if (user?.is_manager) {
				const agentDocs = await db.collection("users").where("manager", "==", auth.currentUser.uid).orderBy("first_name").get()

				// Add the current user into the agentArr
				agentsArr[auth.currentUser.uid] = `${user?.first_name} ${user?.last_name}`

				agentDocs.forEach((agentDoc) => {
					const { first_name, last_name } = agentDoc.data()
					agentsArr[agentDoc.id] = `${first_name} ${last_name}`
				})
			} else {
				agentsArr[auth.currentUser.uid] = `${user?.first_name} ${user?.last_name}`
			}

			setAgents(agentsArr)
		} catch (error) {
			console.error("Error fetching agents:", error)
		}
	}, [user])

	// Fetch all hotels from the database
	const fetchAllHotels = useCallback(async () => {
		try {
			const hotelDocs = await db.collection("hotels").get()
			const hotelsObj = {}
			hotelDocs.forEach((doc) => {
				hotelsObj[doc.id] = doc.data()
			})
			setHotelsMap(hotelsObj)
		} catch (error) {
			console.error("Error fetching hotels:", error)
		}
	}, [])

	// Fetch all courses from the database
	const fetchAllCourses = useCallback(async () => {
		try {
			const courseDocs = await db.collection("courses").get()
			const coursesObj = {}
			courseDocs.forEach((doc) => {
				coursesObj[doc.id] = doc.data()
			})
			setCoursesMap(coursesObj)
		} catch (error) {
			console.error("Error fetching courses:", error)
		}
	}, [])

	// Fetch all transfers from the database
	const fetchAllTransfers = useCallback(async () => {
		try {
			const transferDocs = await db.collection("transfers").get()
			const transfersObj = {}
			transferDocs.forEach((doc) => {
				transfersObj[doc.id] = doc.data()
			})
			setTransfersMap(transfersObj)
		} catch (error) {
			console.error("Error fetching transfers:", error)
		}
	}, [])

	// On component load - fetch agents
	useEffect(() => {
		if (user) {
			fetchAgents()
		}
	}, [user, fetchAgents])

	// On component load - fetch system data
	useEffect(() => {
		if (user) {
			fetchAllHotels()
			fetchAllCourses()
			fetchAllTransfers()
		}
	}, [user, fetchAllHotels, fetchAllCourses, fetchAllTransfers])

	// Cancel ongoing requests when filters change
	useEffect(() => {
		cancelCurrentRequest()
		clearDebounceTimer()
	}, [year, month, agent, type, flightsOnly, cancelCurrentRequest, clearDebounceTimer])

	// On component load - generate years
	useEffect(() => {
		// Create an empty object for storing the years
		let yearsObj = {}
		// Get next year as a number
		let nextYear = Number(moment().startOf(year).add(2, "years").format("YYYY"))
		// Run through a for loop to print out the years
		for (let i = nextYear; i >= 2018; i--) {
			yearsObj = {
				[i]: i,
				...yearsObj,
			}
		}
		// Set the years into the state
		setYears(yearsObj)
	}, [])

	// Debounced fetch function to prevent rapid successive requests
	const debouncedFetch = useCallback(
		(fetchFunction) => {
			clearDebounceTimer()
			debounceTimerRef.current = setTimeout(() => {
				fetchFunction()
			}, 300) // 300ms debounce delay
		},
		[clearDebounceTimer]
	)

	// Retry mechanism for failed requests
	const retryRequest = useCallback(async (requestFunction, maxRetries = 3) => {
		let lastError
		for (let attempt = 1; attempt <= maxRetries; attempt++) {
			try {
				return await requestFunction()
			} catch (error) {
				lastError = error
				if (error.code === "cancelled") {
					throw error // Don't retry cancelled requests
				}
				if (attempt === maxRetries) {
					throw lastError
				}
				// Wait before retrying (exponential backoff)
				await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 1000))
			}
		}
	}, [])

	// Fetch the travellers from the database with proper request management
	const fetchTravellers = useCallback(async () => {
		// Prevent multiple simultaneous requests
		if (isRequestInProgressRef.current) {
			return
		}

		// Cancel any ongoing request
		cancelCurrentRequest()

		// Clear the local figures
		clearLocalFigures()

		// Show a loading spinner
		setLoading(true)
		isRequestInProgressRef.current = true

		try {
			if (year && !month && !agent && user.is_admin) {
				await byYear()
			} else if (year && month && !agent && user.is_admin) {
				await byYearAndMonth()
			} else if (year && month && agent) {
				await byYearMonthAndAgent()
			} else {
				setLoading(false)
			}
		} catch (error) {
			console.error("Error fetching travellers:", error)
			setLoading(false)
		} finally {
			isRequestInProgressRef.current = false
		}
	}, [year, month, agent, user, cancelCurrentRequest])

	// Fetch by year with request cancellation
	const byYear = async () => {
		// Cancel any ongoing request
		cancelCurrentRequest()

		// Get the milliseconds timestamps from the chosen filters
		const fetchStart = moment(`${year}`, "YYYY").startOf("year").valueOf()
		const fetchEnd = moment(`${year}`, "YYYY").endOf("year").valueOf()
		// Build some firestore timestamps from the milliseconds
		const startTimestamp = firebase.firestore.Timestamp.fromMillis(fetchStart)
		const endTimestamp = firebase.firestore.Timestamp.fromMillis(fetchEnd)
		// Setup a variable to store the field we are going to be searching the database with
		let searchBy = "check_in"
		// Are we fetching the bookings by travel or booked date?
		if (type === "travel_date") {
			searchBy = "check_in"
		} else if (type === "booked_date") {
			searchBy = "booked"
		}

		try {
			// Pull all the bookings by their travel date
			const query = db.collection("bookings").where(`${searchBy}`, ">=", startTimestamp).where(`${searchBy}`, "<=", endTimestamp).where("removed", "==", false).orderBy(`${searchBy}`)

			// Store the current request for potential cancellation
			currentRequestRef.current = query

			const bookingDocs = await retryRequest(async () => {
				// Check if this request was cancelled before making the call
				if (currentRequestRef.current !== query) {
					throw { code: "cancelled" }
				}
				return query.get()
			})

			// Check if this request was cancelled
			if (currentRequestRef.current !== query) {
				return
			}

			/**
			 * Create an empty array for storing the bookings
			 */
			let bookingsArr = []
			/**
			 * Setup some variables for storing the totals
			 */
			let totalFlightsCost = 0
			let totalCustomerPrices = 0
			let totalPaidByClients = 0
			let totalSupplierInvoices = 0
			let totalSalesMargins = 0
			let totalCommissions = 0
			/**
			 * Loop through the bookings that have been found that match
			 */
			bookingDocs.forEach((bookingDoc) => {
				/**
				 * Deconstruct some balances from the booking document
				 */
				const { paid_by_client, margins } = bookingDoc.data()
				/**
				 * Pull the margins from the
				 */
				const { customer_price, flights_cost, supplier_invoices, agents_margin } = margins || {}

				// Are we looking for only those bookings with a flights value attached?
				if ((flightsOnly && flights_cost > 0) || (!flightsOnly && flights_cost >= 0)) {
					/**
					 * Check to make sure there is a customer price and supplier invoice figures
					 */
					if (margins && customer_price > 0 && supplier_invoices > 0) {
						/**
						 * work out the sales margin for the booking
						 */
						const salesMargin = Number.parseFloat(customer_price - supplier_invoices)
						/**
						 * Add the values from this booking
						 */
						totalFlightsCost = totalFlightsCost + Number.parseFloat(flights_cost || 0)
						totalCustomerPrices = totalCustomerPrices + Number.parseFloat(customer_price || 0)
						totalPaidByClients = totalPaidByClients + Number.parseFloat(paid_by_client || 0)
						totalSupplierInvoices = totalSupplierInvoices + Number.parseFloat(supplier_invoices || 0)
						totalSalesMargins = totalSalesMargins + salesMargin
						totalCommissions = totalCommissions + Number.parseFloat(agents_margin || 0)
						/**
						 * Push the booking data into the array
						 */
						bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data() })
					} else {
						/**
						 * Push the booking data into the array
						 */
						bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data(), missing_values: true })
					}
				}
			})
			/**
			 * Set the bookings into the state
			 */
			setBookings(bookingsArr)
			/**
			 * Set the totals into the state
			 */
			setFlightsCost(totalFlightsCost || 0)
			setCustomerPrice(totalCustomerPrices || 0)
			setPaidByClient(totalPaidByClients || 0)
			setSupplierInvoices(totalSupplierInvoices || 0)
			setMargins(totalSalesMargins || 0)
			setCommission(totalCommissions || 0)
		} catch (error) {
			if (error.code !== "cancelled") {
				console.error("Error in byYear:", error)
			}
		} finally {
			/**
			 * Reset the state
			 */
			setLoading(false)
			currentRequestRef.current = null
		}
	}

	const byYearAndMonth = async () => {
		// Cancel any ongoing request
		cancelCurrentRequest()

		/**
		 * Get the milliseconds timestamps from the chosen filters
		 */
		const fetchStart = moment(`${month}-${year}`, "MMMM-YYYY").startOf("month").valueOf()
		const fetchEnd = moment(`${month}-${year}`, "MMMM-YYYY").endOf("month").valueOf()
		/**
		 * Build some firestore timestamps from the milliseconds
		 */
		const startTimestamp = firebase.firestore.Timestamp.fromMillis(fetchStart)
		const endTimestamp = firebase.firestore.Timestamp.fromMillis(fetchEnd)
		/**
		 * Setup a variable to store the field we are going to be searching the database with
		 */
		let searchBy = "check_in"
		/**
		 * Are we fetching the bookings by travel or booked date?
		 */
		if (type === "travel_date") {
			searchBy = "check_in"
		} else if (type === "booked_date") {
			searchBy = "booked"
		}

		try {
			// Query the database for all the bookings that match the requirements
			const query = db.collection("bookings").where(`${searchBy}`, ">=", startTimestamp).where(`${searchBy}`, "<=", endTimestamp).where("removed", "==", false).orderBy(`${searchBy}`)

			// Store the current request for potential cancellation
			currentRequestRef.current = query

			const bookingDocs = await retryRequest(async () => {
				// Check if this request was cancelled before making the call
				if (currentRequestRef.current !== query) {
					throw { code: "cancelled" }
				}
				return query.get()
			})

			// Check if this request was cancelled
			if (currentRequestRef.current !== query) {
				return
			}

			/**
			 * Create an empty array for storing the bookings
			 */
			let bookingsArr = []
			/**
			 * Setup some variables for storing the totals
			 */
			let totalFlightsCost = 0
			let totalCustomerPrices = 0
			let totalPaidByClients = 0
			let totalSupplierInvoices = 0
			let totalSalesMargins = 0
			let totalCommissions = 0
			/**
			 * Loop through the bookings that have been found that match
			 */
			bookingDocs.forEach((bookingDoc) => {
				/**
				 * Deconstruct some balances from the booking document
				 */
				const { paid_by_client, margins } = bookingDoc.data()

				// Pull the margins from the booking
				const { customer_price, flights_cost, supplier_invoices, agents_margin } = margins || {}

				// Are we looking for only those bookings with a flights value attached?
				if ((flightsOnly && flights_cost > 0) || (!flightsOnly && flights_cost >= 0)) {
					// Check to make sure there is a customer price and supplier invoice figures
					if (margins && customer_price > 0 && supplier_invoices > 0) {
						// Work out the sales margin for the booking
						const salesMargin = Number.parseFloat(customer_price - supplier_invoices)

						// Add the values from this booking
						totalFlightsCost = totalFlightsCost + Number.parseFloat(flights_cost || 0)
						totalCustomerPrices = totalCustomerPrices + Number.parseFloat(customer_price || 0)
						totalPaidByClients = totalPaidByClients + Number.parseFloat(paid_by_client || 0)
						totalSupplierInvoices = totalSupplierInvoices + Number.parseFloat(supplier_invoices || 0)
						totalSalesMargins = totalSalesMargins + salesMargin
						totalCommissions = totalCommissions + Number.parseFloat(agents_margin || 0)

						// Push the booking data into the array
						bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data() })
					} else {
						// Push the booking into the array without the additional values
						bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data(), missing_values: true })
					}
				}
			})
			/**
			 * Set the bookings into the state
			 */
			setBookings(bookingsArr)
			/**
			 * Set the totals into the state
			 */
			setFlightsCost(totalFlightsCost || 0)
			setCustomerPrice(totalCustomerPrices || 0)
			setPaidByClient(totalPaidByClients || 0)
			setSupplierInvoices(totalSupplierInvoices || 0)
			setMargins(totalSalesMargins || 0)
			setCommission(totalCommissions || 0)
		} catch (error) {
			if (error.code !== "cancelled") {
				console.error("Error in byYearAndMonth:", error)
			}
		} finally {
			/**
			 * Reset the state
			 */
			setLoading(false)
			currentRequestRef.current = null
		}
	}

	const byYearMonthAndAgent = async () => {
		// Cancel any ongoing request
		cancelCurrentRequest()

		/**
		 * Get the milliseconds timestamps from the chosen filters
		 */
		const fetchStart = moment(`${month}-${year}`, "MMMM-YYYY").startOf("month").valueOf()
		const fetchEnd = moment(`${month}-${year}`, "MMMM-YYYY").endOf("month").valueOf()
		/**
		 * Build some firestore timestamps from the milliseconds
		 */
		const startTimestamp = firebase.firestore.Timestamp.fromMillis(fetchStart)
		const endTimestamp = firebase.firestore.Timestamp.fromMillis(fetchEnd)
		/**
		 * Setup a variable to store the field we are going to be searching the database with
		 */
		let searchBy = "check_in"
		/**
		 * Are we fetching the bookings by travel or booked date?
		 */
		if (type === "travel_date") {
			searchBy = "check_in"
		} else if (type === "booked_date") {
			searchBy = "booked"
		}

		try {
			/**
			 * Pull all the bookings by their travel date
			 */
			const query = db
				.collection("bookings")
				.where(`${searchBy}`, ">=", startTimestamp)
				.where(`${searchBy}`, "<=", endTimestamp)
				.where("agent", "==", agent.option)
				.where("removed", "==", false)
				.orderBy(`${searchBy}`)

			// Store the current request for potential cancellation
			currentRequestRef.current = query

			const bookingDocs = await retryRequest(async () => {
				// Check if this request was cancelled before making the call
				if (currentRequestRef.current !== query) {
					throw { code: "cancelled" }
				}
				return query.get()
			})

			// Check if this request was cancelled
			if (currentRequestRef.current !== query) {
				return
			}

			/**
			 * Create an empty array for storing the bookings
			 */
			let bookingsArr = []
			/**
			 * Setup some variables for storing the totals
			 */
			let totalFlightsCost = 0
			let totalCustomerPrices = 0
			let totalPaidByClients = 0
			let totalSupplierInvoices = 0
			let totalSalesMargins = 0
			let totalCommissions = 0
			/**
			 * Loop through the bookings that have been found that match
			 */
			bookingDocs.forEach((bookingDoc) => {
				/**
				 * Deconstruct some balances from the booking document
				 */
				const { paid_by_client, margins } = bookingDoc.data()
				/**
				 * Pull the margins from the
				 */
				const { customer_price, flights_cost, supplier_invoices, agents_margin } = margins || {}

				// Are we looking for only those bookings with a flights value attached?
				if ((flightsOnly && flights_cost > 0) || (!flightsOnly && flights_cost >= 0)) {
					/**
					 * Check to make sure there is a customer price and supplier invoice figures
					 */
					if (margins && customer_price > 0 && supplier_invoices > 0) {
						/**
						 * work out the sales margin for the booking
						 */
						const salesMargin = Number.parseFloat(customer_price - supplier_invoices)
						/**
						 * Add the values from this booking
						 */
						totalFlightsCost = totalFlightsCost + Number.parseFloat(flights_cost || 0)
						totalCustomerPrices = totalCustomerPrices + Number.parseFloat(customer_price || 0)
						totalPaidByClients = totalPaidByClients + Number.parseFloat(paid_by_client || 0)
						totalSupplierInvoices = totalSupplierInvoices + Number.parseFloat(supplier_invoices || 0)
						totalSalesMargins = totalSalesMargins + salesMargin
						totalCommissions = totalCommissions + Number.parseFloat(agents_margin || 0)
						/**
						 * Push the booking data into the array
						 */
						bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data() })
					} else {
						/**
						 * Push the booking data into the array
						 */
						bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data(), missing_values: true })
					}
				}
			})
			/**
			 * Set the bookings into the state
			 */
			setBookings(bookingsArr)
			/**
			 * Set the totals into the state
			 */
			setFlightsCost(totalFlightsCost || 0)
			setCustomerPrice(totalCustomerPrices || 0)
			setPaidByClient(totalPaidByClients || 0)
			setSupplierInvoices(totalSupplierInvoices || 0)
			setMargins(totalSalesMargins || 0)
			setCommission(totalCommissions || 0)
		} catch (error) {
			if (error.code !== "cancelled") {
				console.error("Error in byYearMonthAndAgent:", error)
			}
		} finally {
			/**
			 * Reset the state
			 */
			setLoading(false)
			currentRequestRef.current = null
		}
	}

	const getTableHeadings = () => {
		const headings = [
			"Booked",
			"Reference",
			showSite && "Site",
			showAgent && "Agent",
			showLeadPax && "Lead pax",
			showEmail && user?.is_super_admin && "Email",
			"Travelling",
			showReturning && "Returning",
			showNoPax && "No. of pax",
			showLocation && "Location",
			"Flights",
			"Holiday cost",
			"Paid by client",
			"Supplier invoice",
			showItemisedInvoices && "Itemised invoices",
			"Supplier invoices",
			"Supplier documents",
			"Sales margin",
			"Commission",
			"Confirmed",
			"Resorts paid",
			"",
		].filter(Boolean)
		return headings
	}

	const updateLocalFigures = (id, type, figure) => {
		if (isNaN(figure)) return

		if (type === "MARGIN") {
			totalMarginStatsFigure.current[id] = figure
		} else if (type === "COMMISION") {
			totalCommisionStatsFigure.current[id] = figure
		}

		setNewMargins(
			Object.values(totalMarginStatsFigure.current)
				.reduce((a, b) => a + b, 0)
				?.toLocaleString()
		)
		setNewCommission(
			Object.values(totalCommisionStatsFigure.current)
				.reduce((a, b) => a + b, 0)
				?.toLocaleString()
		)
	}

	const updateLocalPAX = (id, pax = 0, flying = false) => {
		if (flying) {
			paxWithFlights.current[id] = pax
		} else {
			paxWithoutFlights.current[id] = pax
		}

		setPaxFlying(
			Object.values(paxWithFlights.current)
				.reduce((a, b) => a + b, 0)
				?.toLocaleString()
		)
		setPaxNotFlying(
			Object.values(paxWithoutFlights.current)
				.reduce((a, b) => a + b, 0)
				?.toLocaleString()
		)
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Itemised Stats</h1>
			</Title>

			<div className="page-filters extra-stats">
				<Select
					label="Agent:"
					placeholder="Agent:"
					value={agent}
					selected={agent.option}
					activeOnHover={true}
					onSelect={(option) => setAgent(option)}
					options={agents}
				/>

				<Select
					label="Month:"
					placeholder="Month:"
					value={month}
					activeOnHover={true}
					onSelect={(option) => setMonth(option.option)}
					options={CALENDAR_MONTHS}
				/>

				<Select
					label="Year:"
					placeholder="Year:"
					value={year}
					activeOnHover={true}
					onSelect={(option) => setYear(option.option)}
					options={years}
				/>

				<Select
					label="Search by:"
					placeholder="Search by:"
					value={type}
					selected={type}
					activeOnHover={true}
					onSelect={(option) => setType(option.option)}
					options={{
						travel_date: "Travel date",
						booked_date: "Booked date",
					}}
				/>

				<Checkbox
					checked={flightsOnly}
					onClick={() => setFlightsOnly(!flightsOnly)}
					label="Only inc. flights"
				/>

				<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
					<Button
						filters={true}
						disabled={((!agent || !month || !year) && (!month || !year) && !year) || !type}
						label="Fetch stats"
						loadingText="Fetching stats..."
						loading={loading}
						onClick={() => debouncedFetch(fetchTravellers)}
					/>

					{/* {user?.can_export && (
						<div>
							<Button
								filters={true}
								disabled={exportArr.length === 0}
								label="Export to XLSX"
								onClick={() => exportToExcel(exportArr, exportTitle)}
							/>
						</div>
					)} */}
				</div>
			</div>

			<div
				className="column-toggles"
				style={{ display: "flex", gap: "10px", flexWrap: "wrap", padding: "0 0 15px", marginBottom: "25px", borderBottom: "1px solid #e0e0e0" }}>
				<Checkbox
					checked={showSite}
					onClick={() => setShowSite(!showSite)}
					label="Show Site"
				/>
				<Checkbox
					checked={showAgent}
					onClick={() => setShowAgent(!showAgent)}
					label="Show Agent"
				/>
				<Checkbox
					checked={showLeadPax}
					onClick={() => setShowLeadPax(!showLeadPax)}
					label="Show Lead Pax"
				/>
				<Checkbox
					checked={showEmail}
					onClick={() => setShowEmail(!showEmail)}
					label="Show Email"
				/>
				<Checkbox
					checked={showReturning}
					onClick={() => setShowReturning(!showReturning)}
					label="Show Returning"
				/>
				<Checkbox
					checked={showNoPax}
					onClick={() => setShowNoPax(!showNoPax)}
					label="Show No. of Pax"
				/>
				<Checkbox
					checked={showLocation}
					onClick={() => setShowLocation(!showLocation)}
					label="Show Location"
				/>
				<Checkbox
					checked={showItemisedInvoices}
					onClick={() => setShowItemisedInvoices(!showItemisedInvoices)}
					label="Show Itemised Invoices"
				/>
				<div style={{ width: "100%", height: "1px", background: "#e0e0e0", margin: "5px 0" }}></div>
				<Checkbox
					checked={hideWithDocuments}
					onClick={() => setHideWithDocuments(!hideWithDocuments)}
					label="Hide bookings with documents"
				/>
			</div>

			<Table
				className="travellers-table"
				headings={getTableHeadings()}
				noResults={Object.entries(bookings).length === 0}
				noResultsMessage={"No results matching those filters"}>
				{bookings
					.filter((booking) => {
						// If hideWithDocuments is enabled, filter out bookings that have documents
						if (hideWithDocuments) {
							const hasDocuments = documentsMap[String(booking.reference)]?.length > 0
							return !hasDocuments
						}
						return true
					})
					.map((booking) => (
						<Booking
							key={booking?.id}
							id={booking?.id}
							details={booking}
							client_prefetch={showLeadPax || showEmail ? clientsMap[booking.client] : null}
							agent_prefetch={showAgent ? agentsMapPrefetch[booking.agent] : null}
							documents_prefetch={documentsMap[String(booking.reference)] || []}
							supplier_invoices_prefetch={supplierInvoicesMap[booking.id] || []}
							booking_hotels_prefetch={bookingHotelsMap[booking.id] || []}
							booking_rounds_prefetch={bookingRoundsMap[booking.id] || []}
							hotels_map={hotelsMap}
							courses_map={coursesMap}
							transfers_map={transfersMap}
							show_site={showSite}
							show_agent={showAgent}
							show_lead_pax={showLeadPax}
							show_email={showEmail && user?.is_super_admin}
							show_returning={showReturning}
							show_no_pax={showNoPax}
							show_location={showLocation}
							show_itemised_invoices={showItemisedInvoices}
							update_local_totals={(id, type, figure) => updateLocalFigures(id, type, figure)}
							update_local_pax={(id, pax, flying) => updateLocalPAX(id, pax, flying)}
						/>
					))}

				<tr className="totals-row">
					<td
						colSpan={
							[
								true, // Booked
								true, // Reference
								showSite,
								showAgent,
								showLeadPax,
								showEmail && user?.is_super_admin,
								true, // Travelling
								showReturning,
							].filter(Boolean).length
						}>
						Totals:
					</td>
					{showNoPax && (
						<td>
							{paxFlying} (F) - {paxNotFlying} (NF)
						</td>
					)}
					{showLocation && <td></td>}
					<td>£{flightsCost?.toLocaleString() || 0}</td>
					<td>£{customerPrice?.toLocaleString() || 0}</td>
					<td>£{paidByClient?.toLocaleString() || 0}</td>
					<td>£{supplierInvoices?.toLocaleString() || 0}</td>
					{showItemisedInvoices && <td></td>}
					<td></td>
					<td></td>
					<td>£{newMargins || 0}</td>
					<td>£{newCommission || 0}</td>
				</tr>
			</Table>
		</Tile>
	)
}
